/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { GiftCard, GiftCardStatus } from "@lemmonhq/types";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { GiftCardDetailSheet, GiftCardsTable } from "./components";

// Gift cards only; table credits (and their pending/forfeited states) are
// never listed here.
type StatusFilter = "all" | Exclude<GiftCardStatus, "pending" | "forfeited">;

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  redeemed: "Redeemed",
  expired: "Expired",
  void: "Void",
};

const LIMIT = 50;

export const LoyaltyGiftCardsPage = defineComponent({
  name: "LoyaltyGiftCardsPage",
  setup() {
    const trpc = useTrpc();
    const { t } = useLocalization();

    const cards = ref<GiftCard[]>([]);
    const total = ref(0);
    const loading = ref<boolean | Error>(true);
    const statusFilter = ref<StatusFilter>("all");
    const searchQuery = ref("");
    const selectedCard = ref<GiftCard | null>(null);
    const offset = ref(0);

    const loadCards = async () => {
      loading.value = true;
      try {
        const result = await trpc.giftCards.list.query({
          status: statusFilter.value === "all" ? undefined : statusFilter.value,
          search: searchQuery.value || undefined,
          limit: LIMIT,
          offset: offset.value,
        });
        if (result) {
          cards.value = result.data;
          total.value = result.total;
        }
      } catch (e: any) {
        loading.value = e instanceof Error ? e : new Error(String(e));
      } finally {
        if (loading.value === true) loading.value = false;
      }
    };

    onMounted(loadCards);

    const handleVoid = async (id: string) => {
      await trpc.giftCards.void.mutate({ gift_card_id: id });
      await loadCards();
    };

    const handleFilterChange = (s: StatusFilter) => {
      statusFilter.value = s;
      offset.value = 0;
      loadCards();
    };

    const handleSearchChange = (q: string) => {
      searchQuery.value = q;
      offset.value = 0;
      loadCards();
    };

    return () => (
      <AppSubpage>
        <AppSubpageHeader title="Gift Cards" icon="card_giftcard">
          <div class="flex gap-2 flex-wrap items-center">
            {(["all", "active", "redeemed", "void"] as StatusFilter[]).map((s) => (
              <button
                key={s}
                class={`btn btn-sm ${statusFilter.value === s ? "btn-primary" : "btn-ghost"}`}
                onClick={() => handleFilterChange(s)}
              >
                {s === "all" ? "All" : STATUS_LABELS[s]}
              </button>
            ))}
            <input
              class="input input-bordered input-sm w-48"
              placeholder="Search code…"
              value={searchQuery.value}
              onInput={(e) => handleSearchChange((e.target as HTMLInputElement).value)}
            />
          </div>
        </AppSubpageHeader>

        <AppSubpageContent>
          {loading.value === true ? (
            <div class="flex justify-center py-16">
              <span class="loading loading-spinner loading-lg" />
            </div>
          ) : loading.value instanceof Error ? (
            <div class="alert alert-error m-4">
              <span>Failed to load gift cards: {loading.value.message}</span>
            </div>
          ) : cards.value.length === 0 ? (
            <div class="flex flex-col items-center justify-center py-20 gap-4 text-base-content/40">
              <span class="material-symbols-outlined text-6xl">card_giftcard</span>
              <p>No gift cards found</p>
            </div>
          ) : (
            <GiftCardsTable
              cards={cards.value}
              total={total.value}
              offset={offset.value}
              limit={LIMIT}
              onRowClick={(card) => (selectedCard.value = card)}
              onPrev={() => {
                offset.value = Math.max(0, offset.value - LIMIT);
                loadCards();
              }}
              onNext={() => {
                offset.value += LIMIT;
                loadCards();
              }}
            />
          )}
        </AppSubpageContent>

        <GiftCardDetailSheet
          card={selectedCard.value}
          onClose={() => (selectedCard.value = null)}
          onVoid={handleVoid}
        />
      </AppSubpage>
    );
  },
});
